<template>
    <div class="navigator-table-wrapper">
        <div v-if="title" class="navigator-table-title">{{ title }}</div>
        <div class="navigator-table-scroll" v-html="renderedTable"></div>
    </div>
</template>

<script>
import { computed } from 'vue';

export default {
    name: 'NavigatorDataTable',
    props: {
        title: { type: String, default: '' },
        content: { type: String, default: '' },
        sortable: { type: Boolean, default: false },
    },
    setup(props) {
        const renderedTable = computed(() => {
            if (!props.content) return '';

            const lines = props.content.trim().split('\n').filter(l => l.trim());
            if (lines.length < 2) return `<p>${props.content}</p>`;

            const parseRow = (line) => {
                return line.split('|').map(cell => cell.trim()).filter(cell => cell.length > 0);
            };

            const headers = parseRow(lines[0]);
            const dataLines = lines.slice(2);

            let html = '<table class="navigator-table"><thead><tr>';
            headers.forEach(h => { html += `<th>${h}</th>`; });
            html += '</tr></thead><tbody>';

            dataLines.forEach(line => {
                const cells = parseRow(line);
                html += '<tr>';
                cells.forEach((cell, i) => {
                    const isNumeric = i > 0 && /^[\$\-\+\d%]/.test(cell.trim());
                    const cls = isNumeric ? ' class="text-mono text-end"' : '';
                    html += `<td${cls}>${cell}</td>`;
                });
                html += '</tr>';
            });

            html += '</tbody></table>';
            return html;
        });

        return { renderedTable };
    },
};
</script>

<style scoped>
.navigator-table-wrapper {
    overflow: hidden;
    border-radius: var(--compass-radius-sm, 8px);
    border: 1px solid var(--nav-card-border, rgba(255, 255, 255, 0.06));
}

.navigator-table-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--nav-text, #e5e7eb);
    padding: 12px 16px;
    background: var(--nav-card-bg, rgba(139, 92, 246, 0.05));
    border-bottom: 1px solid var(--nav-card-border, rgba(255, 255, 255, 0.06));
}

.navigator-table-scroll { overflow-x: auto; }

.navigator-table-scroll :deep(.navigator-table) {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.navigator-table-scroll :deep(th) {
    padding: 10px 14px;
    background: var(--nav-table-header-bg, rgba(0, 0, 0, 0.3));
    color: var(--nav-text-muted, #9ca3af);
    font-weight: 600;
    text-align: left;
    white-space: nowrap;
    border-bottom: 1px solid var(--nav-table-border, rgba(255, 255, 255, 0.06));
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.navigator-table-scroll :deep(td) {
    padding: 10px 14px;
    color: var(--nav-text, #d1d5db);
    border-bottom: 1px solid var(--nav-border-subtle, rgba(255, 255, 255, 0.03));
}

.navigator-table-scroll :deep(tr:hover td) {
    background: var(--nav-hover, rgba(139, 92, 246, 0.04));
}

.navigator-table-scroll :deep(.text-mono) {
    font-family: 'JetBrains Mono', monospace;
}

.navigator-table-scroll :deep(.text-end) {
    text-align: right;
}

.navigator-table-scroll :deep(td strong) { color: var(--nav-text, #f3f4f6); }

.navigator-table-scroll :deep(td code) {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    background: var(--nav-code-bg, rgba(139, 92, 246, 0.08));
    padding: 1px 5px;
    border-radius: 3px;
    color: var(--nav-code-text, #c4b5fd);
}
</style>
